import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  addCohortRow,
  IMeasureFilterValue,
  IRowMeta,
  RowType,
  setMeasureFiltersAction,
} from '../../../redux/slices/oldBuilder';
import { CohortRow } from '../Cohorts';
import * as BuilderUI from '../BuilderUI';
import SectionHeading from '../../SectionHeading';
import ReportFilterMenu from './ReportFilterMenu';
import { MaybeNil } from '../../../utils/interfaces';
import {
  FieldContext,
  IFilterFieldsFragment,
  QueryType,
  TagOption,
} from '../../../utils/apollo/graphqlSchema';
import {
  filterPrimitiveSelector,
  measureFiltersSelector,
} from '../../../redux/selectors/builder';
import Popover from '../../Popover';
import { GlobalFilter, GlobalFilterPredicatePopover } from './GlobalFilter';
import { MAX_GLOBAL_COHORTS } from '../../../config/constants';
import useQueryTypeFromLocation from '../../../utils/routes/useQueryTypeFromLocation';
import BuilderSectionCard from '../BuilderSectionCard';
import {
  MeasureFilterDefaultView,
  MeasureFilterDisplay,
  MeasureFilterForm,
} from './MeasureFilter';
import isNil from 'lodash/isNil';
import { assertNotNil } from '../../../utils/types';
import DndDroppableZone from '../../DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../QuickAccessDictionary/DictionaryTabs/types';
import { useDndEntityInternalContext } from '../../DndEntity';
import DropzoneArea from '../../DndEntity/DndDroppableZone/DropzoneArea';
import {
  EntityDropzoneDataTypeEnum,
  ICohortEntityDropzoneDataType,
  IEventEntityDropzoneDataType,
  IFieldEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
  INamedFilterEntityDropzoneDataType,
} from '../../DndEntity/validation';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../DndEntity/utils';
import { createTagOptionInput } from '../EntitySelection/utils';
import { defaultEventInput } from '../../../utils/builder';
import FieldDroppableZone from '../../DndEntity/DndDroppableZone/FieldDroppableZone';
import useQueryBuilderDroppable from '../../DndEntity/useQueryBuilderDroppable';
import usePopoverController from '../../Popover/usePopoverController';

const globalCohortRowType: RowType = RowType.globalCohorts;

/**
 * Global filters use static RowMeta to set or retrieve filter. Using `freeze`
 * prevents accidental modification of this shared variable
 */
const globalFilterRowMeta: IRowMeta = Object.freeze({
  rowType: RowType.global,
  rowIndex: -1, // -1 only to indicate global filter
});

const ReportFilter = () => {
  const [measureFilterView, setMeasureFilterView] = useState<1 | 2 | null>(
    null,
  );
  const [selectedDefaultMeasureKey, setSelectedDefaultMeasureKey] =
    useState('');

  const { closePopover, isPopoverOpen, openPopover, popoverAnchor } =
    usePopoverController<HTMLDivElement>();

  const dispatch = useAppDispatch();

  const queryType = useQueryTypeFromLocation();
  assertNotNil(queryType);

  const { isBasicBuilderComplete } = useQueryBuilderDroppable();

  const globalCohorts = useAppSelector(
    ({ builder }) => builder[globalCohortRowType],
  );
  const { activeItem, setDefaultFilter, popoverId, setPopoverId } =
    useDndEntityInternalContext();

  const filterSelector = useMemo(filterPrimitiveSelector, []);
  const globalFilter: MaybeNil<IFilterFieldsFragment> = useAppSelector(
    (state) => filterSelector(state, globalFilterRowMeta),
  );

  const globalFilterConditionsLength = globalFilter?.conditions?.length ?? 0;

  const measureFilters = useAppSelector(measureFiltersSelector);

  const activeMeasureFilters = useMemo(() => {
    if (!measureFilters?.length) {
      return [];
    }

    return measureFilters.filter((measure) => !isNil(measure.filter));
  }, [measureFilters]);

  const allowAddGlobalCohort = globalCohorts?.length < MAX_GLOBAL_COHORTS;

  const handleAddGlobalCohort = () => {
    if (!allowAddGlobalCohort) {
      return;
    }

    dispatch(
      addCohortRow({
        rowType: globalCohortRowType,
        limit: MAX_GLOBAL_COHORTS,
      }),
    );
  };

  const handleAddGlobalFilter = () => {
    openPopover();
  };

  const closeGlobalFilterPopover = () => {
    closePopover();
    setPopoverId(null);
    setDefaultFilter(null);
  };

  const handleAddMeasureFilter = () => {
    setMeasureFilterView(!activeMeasureFilters.length ? 1 : 2);
  };

  const handleCloseMeasureFilterDefaultView = () => {
    setMeasureFilterView(null);
  };

  const handleCloseMeasureFilterPopover = () => {
    if (selectedDefaultMeasureKey) {
      setSelectedDefaultMeasureKey('');
    }

    setMeasureFilterView(null);
  };

  const handleSelectDefaultMeasureFilter = (newMeasureKey: string) => {
    setSelectedDefaultMeasureKey(newMeasureKey);

    setMeasureFilterView(2);
  };

  const handleSubmitMeasureFilter = (newFilters: IMeasureFilterValue[]) => {
    dispatch(
      setMeasureFiltersAction({
        activeMeasureFilters,
        newFilters,
      }),
    );

    handleCloseMeasureFilterPopover();
  };

  const fieldsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Fields,
    globalFilterRowMeta,
  );

  const tags = [
    createTagOptionInput(TagOption.Field, {
      field: {
        func: null,
        context: FieldContext.Filter,
        events: [defaultEventInput],
        arrayObjectContext: null,
      },
    }),
  ];

  // DndEntity context keeps track of which filter widget is open
  // by checking the popoverId against the generated fieldsDropzoneId
  // which would then open the filter widget on drop
  useEffect(() => {
    if (
      !isNil(popoverId) &&
      !isNil(fieldsDropzoneId) &&
      popoverId === fieldsDropzoneId
    ) {
      openPopover();
    }
  }, [popoverId, fieldsDropzoneId, openPopover]);

  const allowMeasureFilter =
    queryType === QueryType.Expression || queryType === QueryType.DataTable;

  const showAddFieldDropzone = useMemo(() => {
    if (globalFilter) {
      return false;
    }

    const data = getDndActiveItemData(activeItem);

    if (data && isBasicBuilderComplete) {
      return data.__typename === 'TagLeafField';
    }

    return false;
  }, [activeItem, globalFilter, isBasicBuilderComplete]);

  const showAddFilterDropzone = useMemo(() => {
    if (globalFilter) {
      return false;
    }

    const data = getDndActiveItemData(activeItem);

    if (data && isBasicBuilderComplete) {
      return data.__typename === 'TagLeafFilter';
    }

    return false;
  }, [activeItem, globalFilter, isBasicBuilderComplete]);

  const showAddCohortFilterDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (data && allowAddGlobalCohort && isBasicBuilderComplete) {
      return (
        data.__typename === 'TagLeafSimpleCohort' ||
        data.__typename === 'TagLeafCompoundCohort'
      );
    }

    return false;
  }, [activeItem, allowAddGlobalCohort, isBasicBuilderComplete]);

  const showAddMeasureCohortFilterDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (data && allowAddGlobalCohort && isBasicBuilderComplete) {
      return (
        data.__typename === 'TagLeafSimpleMeasure' ||
        data.__typename === 'TagLeafCompoundMeasure'
      );
    }

    return false;
  }, [activeItem, allowAddGlobalCohort, isBasicBuilderComplete]);

  const showAddPerformedCohortFilterDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (data && allowAddGlobalCohort && isBasicBuilderComplete) {
      return (
        data.__typename === 'TagLeafBasicEvent' ||
        data.__typename === 'TagLeafVirtualEvent'
      );
    }

    return false;
  }, [activeItem, allowAddGlobalCohort, isBasicBuilderComplete]);

  // rowMeta used for cohort filters in report filter
  const cohortFilterRowMeta = {
    rowType: globalCohortRowType,
    rowIndex: globalCohorts?.length ?? 0,
  };
  const cohortsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Cohorts,
    cohortFilterRowMeta,
    'Report-Filter',
  );
  const measuresDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Measures,
    cohortFilterRowMeta,
    'Report-Filter',
  );
  const eventsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Events,
    cohortFilterRowMeta,
    'Report-Filter',
  );
  const filtersDropzoneId = 'Report-Filter-Droppable-Zone';

  return (
    <BuilderUI.SectionColumn>
      <SectionHeading
        heading="Report Filter"
        tooltipOption={{
          content: 'Filter to include or exclude specific data.',
        }}
      />
      {!(
        showAddFilterDropzone ||
        showAddFieldDropzone ||
        showAddCohortFilterDropzone
      ) && (
        <ReportFilterMenu
          ref={popoverAnchor}
          onAddGlobalFilter={handleAddGlobalFilter}
          onAddCohortFilter={handleAddGlobalCohort}
          onAddMeasureFilter={
            allowMeasureFilter ? handleAddMeasureFilter : undefined
          }
          disableCohortFilter={!allowAddGlobalCohort}
          disableMeasureFilter={false}
        />
      )}
      {showAddFilterDropzone && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Filters]}
          id={filtersDropzoneId}
          data={
            {
              id: filtersDropzoneId,
              type: EntityDropzoneDataTypeEnum.Filters,
              rowMeta: globalFilterRowMeta,
            } satisfies INamedFilterEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">
            + Add Field as New Report Filter
          </DropzoneArea>
        </DndDroppableZone>
      )}

      {showAddFieldDropzone && (
        <FieldDroppableZone
          dropzoneId={fieldsDropzoneId}
          tags={tags}
          data={
            {
              id: fieldsDropzoneId,
              type: EntityDropzoneDataTypeEnum.Fields,
              rowMeta: globalFilterRowMeta,
            } satisfies IFieldEntityDropzoneDataType
          }
          hideOnDisabled
        >
          <DropzoneArea size="large">
            + Add Field as New Report Filter
          </DropzoneArea>
        </FieldDroppableZone>
      )}

      {showAddCohortFilterDropzone && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Cohorts]}
          id={cohortsDropzoneId}
          data={
            {
              id: cohortsDropzoneId,
              type: EntityDropzoneDataTypeEnum.Cohorts,
              rowMeta: cohortFilterRowMeta,
            } satisfies ICohortEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">+ Add Cohort Filter</DropzoneArea>
        </DndDroppableZone>
      )}

      {showAddMeasureCohortFilterDropzone && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Measures]}
          id={measuresDropzoneId}
          data={
            {
              id: measuresDropzoneId,
              type: EntityDropzoneDataTypeEnum.Measures,
              rowMeta: cohortFilterRowMeta,
              addCohortRow: true,
            } satisfies IMeasureEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">
            + Add Measure as New Report Filter
          </DropzoneArea>
        </DndDroppableZone>
      )}

      {showAddPerformedCohortFilterDropzone && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Events]}
          id={eventsDropzoneId}
          data={
            {
              id: eventsDropzoneId,
              type: EntityDropzoneDataTypeEnum.Events,
              rowMeta: {
                ...cohortFilterRowMeta,
                eventIndex: 0,
              },
              addCohortRow: true,
            } satisfies IEventEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">
            + Add Event as New Report Filter
          </DropzoneArea>
        </DndDroppableZone>
      )}

      {globalFilter && globalFilterConditionsLength > 0 && (
        <BuilderSectionCard iconName="global-filter">
          <GlobalFilter
            rowMeta={globalFilterRowMeta}
            globalFilter={globalFilter}
          />
        </BuilderSectionCard>
      )}
      {activeMeasureFilters.length > 0 && (
        <BuilderSectionCard iconName="measure-filter">
          <MeasureFilterDisplay />
        </BuilderSectionCard>
      )}
      {globalCohorts?.length > 0 &&
        globalCohorts.map((cohortRow, rowIndex) => (
          <CohortRow
            key={`global-cohort-filter-${rowIndex}`}
            rowMeta={{
              rowType: globalCohortRowType,
              rowIndex,
            }}
            cohortRow={cohortRow}
            allowRemove
          />
        ))}
      {isPopoverOpen && (
        <GlobalFilterPredicatePopover
          rowMeta={globalFilterRowMeta}
          anchorEl={popoverAnchor}
          globalFilter={globalFilter}
          onClose={closeGlobalFilterPopover}
        />
      )}
      {measureFilterView === 1 && (
        <Popover
          isOpen
          close={handleCloseMeasureFilterDefaultView}
          anchor={popoverAnchor.current}
        >
          <MeasureFilterDefaultView
            options={measureFilters}
            onSelect={handleSelectDefaultMeasureFilter}
          />
        </Popover>
      )}
      {measureFilterView === 2 && (
        <Popover
          isOpen
          close={handleCloseMeasureFilterPopover}
          anchor={popoverAnchor.current}
        >
          <MeasureFilterForm
            options={measureFilters}
            onSubmit={handleSubmitMeasureFilter}
            defaultValues={activeMeasureFilters}
            defaultMeasureKey={selectedDefaultMeasureKey}
          />
        </Popover>
      )}
    </BuilderUI.SectionColumn>
  );
};

export default ReportFilter;
